import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { BillingDocument, billingApi, TYPE_LABEL } from '@/services/billingApi';
import { toast } from 'sonner';

type Props = {
  document: BillingDocument;
  onClose: () => void;
  onSent: (doc: BillingDocument) => void;
};

export function BillingSendDialog({ document, onClose, onSent }: Props) {
  const [mailboxes, setMailboxes] = useState<Array<{ id: string; email: string; name: string }>>([]);
  const [chats, setChats] = useState<Array<{ id: string; contactName: string | null; contactPhone: string | null; chatId: string }>>([]);
  const [to, setTo] = useState(document.client?.email || '');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState(`${TYPE_LABEL[document.type]} ${document.numberDisplay || ''}`);
  const [message, setMessage] = useState(
    `Gentile ${document.client?.displayName || ''},\n\nin allegato ${TYPE_LABEL[document.type].toLowerCase()} ${document.numberDisplay || ''}.\n\nCordiali saluti`
  );
  const [caption, setCaption] = useState(`${TYPE_LABEL[document.type]} ${document.numberDisplay || ''}`);
  const [conversationId, setConversationId] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    billingApi.mailboxes().then(setMailboxes).catch(() => undefined);
    if (document.clientId) {
      billingApi.chats(document.clientId).then((list) => {
        setChats(list);
        if (list.length >= 1) setConversationId(list[0].id);
      }).catch(() => undefined);
    }
    billingApi.settings().then((s) => {
      const vars: Record<string, string> = {
        typeLabel: TYPE_LABEL[document.type],
        number: document.numberDisplay || '',
        clientName: document.client?.displayName || '',
        studioName: s.settings?.legalName || 'Studio Gori',
      };
      const fill = (tpl: string) => tpl.replace(/\{\{(\w+)\}\}/g, (_, k) => vars[k] ?? '');
      if (s.settings?.emailSubjectTemplate) setSubject(fill(s.settings.emailSubjectTemplate));
      if (s.settings?.emailBodyTemplate) setMessage(fill(s.settings.emailBodyTemplate));
      if (s.settings?.whatsappCaptionTemplate) setCaption(fill(s.settings.whatsappCaptionTemplate));
    }).catch(() => undefined);
  }, [document]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-lg w-full p-6 space-y-4 shadow-xl">
        <h2 className="text-lg font-semibold">Invia documento</h2>
        <div>
          <Label>Casella</Label>
          <select className="w-full border rounded-md h-10 px-2">
            {mailboxes.length === 0 && <option>Nessuna casella Gmail collegata</option>}
            {mailboxes.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} ({m.email})
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label>Destinatario</Label>
          <Input value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div>
          <Label>CC</Label>
          <Input value={cc} onChange={(e) => setCc(e.target.value)} />
        </div>
        <div>
          <Label>Oggetto</Label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
        </div>
        <div>
          <Label>Messaggio</Label>
          <Textarea rows={5} value={message} onChange={(e) => setMessage(e.target.value)} />
        </div>
        <div className="flex gap-2">
          <Button
            disabled={busy || !to || mailboxes.length === 0}
            onClick={async () => {
              setBusy(true);
              try {
                const d = await billingApi.sendEmail(document.id, { to, cc, subject, message });
                toast.success('Email inviata');
                onSent(d);
              } catch (e: any) {
                toast.error(e.response?.data?.message || 'Invio email fallito');
              } finally {
                setBusy(false);
              }
            }}
          >
            Invia email
          </Button>
          <Button variant="outline" onClick={onClose}>
            Chiudi
          </Button>
        </div>
        <hr />
        <div>
          <Label>Chat WhatsApp</Label>
          <select
            className="w-full border rounded-md h-10 px-2"
            value={conversationId}
            onChange={(e) => setConversationId(e.target.value)}
            disabled={chats.length === 0}
          >
            {chats.length === 0 && (
              <option value="">Nessun numero WhatsApp in anagrafica cliente</option>
            )}
            {chats.map((c) => (
              <option key={c.id} value={c.id}>
                {c.contactName || c.contactPhone || c.chatId}
              </option>
            ))}
          </select>
          {conversationId === 'new' && (
            <p className="text-xs text-muted-foreground mt-1">
              Primo messaggio: si apre una chat sul numero WhatsApp del cliente, non su quello dell&apos;operatore.
            </p>
          )}
        </div>
        <div>
          <Label>Didascalia WhatsApp</Label>
          <Input value={caption} onChange={(e) => setCaption(e.target.value)} />
        </div>
        <Button
          disabled={busy || !conversationId}
          variant="outline"
          onClick={async () => {
            setBusy(true);
            try {
              const d = await billingApi.sendWhatsApp(document.id, { conversationId, caption });
              toast.success('WhatsApp inviato');
              onSent(d);
            } catch (e: any) {
              toast.error(e.response?.data?.message || 'Invio WhatsApp fallito');
            } finally {
              setBusy(false);
            }
          }}
        >
          Invia WhatsApp
        </Button>
      </div>
    </div>
  );
}
